/**
 * Projections for `MachineScene` — the demo's `openMachine()` (line 2176) and
 * its tab bodies (`machineTab`, line 2199).
 *
 * Pure: no React, no fetching. Figures come from `shared/oem-engine.ts`; this
 * file groups, orders, keys and labels them for the machine page's seven tabs.
 *
 * ## Why composite keys matter here
 *
 * `WorkOrder.wo_id` is **not unique** — the same id appears on more than one
 * row of the 3,920-row table. Anything this file returns for a list therefore
 * carries an explicit `key` built from the id plus its index within the
 * machine's own (already sorted) slice, so React never sees a duplicate.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/fleet/MachineScene.tsx
 * @see docs/logic/fleet/machine-page.md
 * @see docs/foundations/4-layer-architecture.md
 */

import {
  BY_ID,
  CLASSES,
  COST_PER_MACHINE,
  ENERGY_FACTORS,
  FLEET,
  IDS,
  MINES,
  RATES,
  WORKINGS,
} from '../../data/core';
import type { CriticalEvent, TrendSeriesRegistry } from '../../schema/alert';
import type { DocIndexEntry, EmailHeader } from '../../schema/document';
import {
  CONDITION_BAND_LABEL,
  type ConditionBand,
  type Machine,
  type MachineClassDef,
  type MineCode,
} from '../../schema/machine';
import type { CostByMachineCategory, CostCategory } from '../../schema/cost';
import type { CataloguePart, InventoryLine } from '../../schema/part';
import type { ServiceCase } from '../../schema/service';
import type {
  DailyMetricName,
  DailyMetricRow,
  EventDailyRow,
  LiveReplayRegistry,
  LiveSample,
  MachineEnergy,
  RmsLiveDrillSample,
  RmsMachineHealth,
  ShiftDailyRow,
} from '../../schema/telemetry';
import type { WorkOrder } from '../../schema/work-order';
import {
  availOf,
  componentRow,
  fmt$,
  groupNum,
  machineComponents,
  statusOf,
  subclassPeers,
  subclassStep,
  wosOf,
  type ComponentRow,
  type ShiftCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

/** Demo line 2176. `openMachine`'s header, as data. */
export interface MachineHeader {
  readonly machine: Machine;
  readonly classDef: MachineClassDef;
  readonly mine: MineCode;
  readonly mineName: string;
  readonly band: ConditionBand;
  /** `'L-02 · ML-21 diesel loader'` — the `<h1>`. */
  readonly title: string;
  /** The demo's single-paragraph standfirst, joined with ` · `. */
  readonly standfirst: string;
  readonly availability: number | null;
  /** `cost_per_op_hour_aud` from the eager per-machine cost row, or `null`. */
  readonly costPerOpHour: number | null;
  /** One-based position among same-subclass peers, and the peer count. */
  readonly position: number;
  readonly peers: number;
  readonly previousId: string;
  readonly nextId: string;
}

/**
 * Demo line 2176. The header.
 *
 * `ctx` is only needed for availability; everything else is eager. Who pays is
 * **not** here: it needs the Maximo record, which arrives on its own fetch, so
 * the scene renders that chip once `loadMachineRecord` resolves.
 */
export function machineHeader(machineId: string, ctx: ShiftCtx): MachineHeader {
  const f = BY_ID[machineId];
  const m = MINES[f.mine];
  const classDef = CLASSES[f.model];
  const cost = COST_PER_MACHINE.find((c) => c.machine_id === machineId) ?? null;
  const peers = subclassPeers(machineId);
  const working = f.working && WORKINGS[f.mine] ? f.working : null;

  const standfirst = [
    `${m.name} (${m.state})`,
    working,
    `${f.division} › ${f.subclass}`,
    `${groupNum(f.engine_hours)} engine h`,
    `commissioned ${f.commissioned}`,
    f.operating_mode,
    f.drive,
    `operated by ${f.operated_by}`,
    m.data_mode === 'Live'
      ? f.cls === 'sdrill'
        ? 'live RMS feed'
        : 'live telematics'
      : 'shift-end data',
    `serial ${f.serial}`,
  ]
    .filter((x): x is string => Boolean(x))
    .join(' · ');

  return {
    machine: f,
    classDef,
    mine: f.mine,
    mineName: m.name,
    band: statusOf(machineId),
    title: `${machineId} · ${classDef.name}`,
    standfirst,
    availability: availOf(machineId, ctx),
    costPerOpHour: cost?.cost_per_op_hour_aud ?? null,
    position: peers.findIndex((x) => x.machine_id === machineId) + 1,
    peers: peers.length,
    previousId: subclassStep(machineId, -1),
    nextId: subclassStep(machineId, 1),
  };
}

// ---------------------------------------------------------------------------
// Condition register
// ---------------------------------------------------------------------------

/** A condition row with the React key the list needs. */
export interface KeyedComponentRow extends ComponentRow {
  readonly key: string;
}

/**
 * Demo line 2177. The register, worst band first then lowest score, each row
 * already expanded to its interval / remaining arithmetic.
 *
 * Component names are unique within a machine's register, so the name is a
 * sufficient key; it is spelled out rather than assumed.
 */
export const conditionRegister = (machineId: string): KeyedComponentRow[] =>
  machineComponents(machineId).map((c) => ({ ...componentRow(c), key: c.component }));

// ---------------------------------------------------------------------------
// Work history
// ---------------------------------------------------------------------------

/** One row of the work-history tab. */
export interface WorkHistoryRow {
  /** `wo_id` plus position — `wo_id` alone is not unique across the table. */
  readonly key: string;
  readonly order: WorkOrder;
  /** `created`, date only. */
  readonly date: string;
  /** Direct cost plus lost production — what the demo prints as the all-in. */
  readonly allIn: number;
  readonly allInLabel: string;
  /** `null` means downtime was never recorded, which is not the same as zero. */
  readonly downtimeHours: number | null;
  readonly isDraft: boolean;
}

/** Totals under the work-history heading. */
export interface WorkHistorySummary {
  readonly rows: readonly WorkHistoryRow[];
  readonly orders: number;
  readonly breakdowns: number;
  readonly preventive: number;
  readonly total: number;
  readonly totalLabel: string;
  /** Distinct components the machine has work against, for the filter chips. */
  readonly components: readonly string[];
}

/**
 * Demo line 2200. `machineTab('history')` — work orders newest first, filtered
 * to one component when the illustration or the register has selected one.
 *
 * `downtime_h` is carried through as `number | null`; the demo prints the
 * downtime clause only when the value is truthy, which collapses "0 h" and
 * "never recorded" into the same blank. This port keeps the demo's display
 * rule but preserves the distinction in the data so a caller can tell them
 * apart.
 */
export function workHistory(
  machineId: string,
  ctx: WorkOrderCtx,
  component?: string | null,
): WorkHistorySummary {
  const all = wosOf(machineId, ctx);
  const filtered = component ? all.filter((w) => w.component === component) : all;

  const rows = filtered.map((order, i): WorkHistoryRow => {
    const allIn = order.total_direct_cost_aud + (order.downtime_cost_aud || 0);
    return {
      key: `${order.wo_id}#${i}`,
      order,
      date: order.created.slice(0, 10),
      allIn,
      allInLabel: fmt$(allIn),
      downtimeHours: order.downtime_h,
      isDraft: order.status.startsWith('Draft'),
    };
  });

  const total = rows.reduce((a, r) => a + r.allIn, 0);

  return {
    rows,
    orders: filtered.length,
    breakdowns: filtered.filter((w) => w.wo_type === 'Breakdown').length,
    preventive: filtered.filter((w) => w.wo_type === 'Preventive').length,
    total,
    totalLabel: fmt$(total),
    components: [...new Set(all.map((w) => w.component))].sort((a, b) => a.localeCompare(b)),
  };
}

// ---------------------------------------------------------------------------
// Cost
// ---------------------------------------------------------------------------

/** One category bar on the cost tab. */
export interface CostCategoryRow {
  readonly category: CostCategory;
  readonly amount: number;
  /** Share of the largest category, 0–100 — the bar's width. */
  readonly share: number;
}

/** Demo line 2219. `CATS` — the five ledger categories, in the demo's order. */
export const COST_CATEGORIES: readonly CostCategory[] = [
  'Labour',
  'Parts',
  'Freight',
  'External service',
  'Lost production',
];

/** The cost tab, as data. */
export interface MachineCostView {
  readonly categories: readonly CostCategoryRow[];
  readonly crews: readonly CostCategoryRow[];
  /** 18-month all-in from the ledger. */
  readonly total: number;
  readonly totalLabel: string;
  readonly costPerOpHour: number | null;
  readonly opHours: number | null;
  /** Mean cost per operating hour across this model's peers. */
  readonly peerMean: number;
  /** The demo's three-way verdict against the peer mean. */
  readonly verdict: string;
  /** Percentage of all-in cost arising from breakdowns. */
  readonly reactiveSharePct: number;
  readonly breakdowns: number;
  readonly preventive: number;
  readonly late: number;
  readonly labourRate: number;
  readonly dc: string;
  readonly freightDays: number;
  readonly lostProductionRate: number | null;
  /** Non-preventive orders, the demo's 25-row table. */
  readonly rows: readonly WorkHistoryRow[];
}

/** Demo line 2219. `machineTab('costs')`. */
export function machineCost(
  machineId: string,
  ctx: WorkOrderCtx,
  ledger: readonly CostByMachineCategory[],
): MachineCostView {
  const f = BY_ID[machineId];
  const rates = RATES[f.mine];
  const machineLedger = ledger.filter((c) => c.machine_id === machineId);
  const total = machineLedger.reduce((a, c) => a + c.amount_aud, 0);
  const cost = COST_PER_MACHINE.find((c) => c.machine_id === machineId) ?? null;

  const peers = COST_PER_MACHINE.filter((c) => BY_ID[c.machine_id].model === f.model);
  const peerMean = peers.length
    ? peers.reduce((a, c) => a + c.cost_per_op_hour_aud, 0) / peers.length
    : 0;

  const orders = wosOf(machineId, ctx);
  const byCrew = new Map<string, number>();
  for (const w of orders) {
    byCrew.set(
      w.crew,
      (byCrew.get(w.crew) ?? 0) + w.total_direct_cost_aud + (w.downtime_cost_aud || 0),
    );
  }

  const catAmounts = COST_CATEGORIES.map((category) => ({
    category,
    amount: machineLedger.find((x) => x.category === category)?.amount_aud ?? 0,
  }));
  const catMax = Math.max(1, ...catAmounts.map((c) => c.amount));

  const crewEntries = [...byCrew.entries()].sort((a, b) => b[1] - a[1]);
  const crewMax = Math.max(1, ...crewEntries.map(([, v]) => v));

  const breakdownCost = orders
    .filter((w) => w.wo_type === 'Breakdown')
    .reduce((a, w) => a + w.total_direct_cost_aud + (w.downtime_cost_aud || 0), 0);

  const cph = cost?.cost_per_op_hour_aud ?? null;

  return {
    categories: catAmounts.map((c) => ({ ...c, share: (c.amount / catMax) * 100 })),
    crews: crewEntries.map(([crew, amount]) => ({
      category: crew as CostCategory,
      amount,
      share: (amount / crewMax) * 100,
    })),
    total,
    totalLabel: fmt$(total),
    costPerOpHour: cph,
    opHours: cost?.op_hours_18m ?? null,
    peerMean,
    verdict:
      cph == null
        ? 'No ledger row for this machine'
        : cph > peerMean * 1.15
          ? 'Above peers — see crew and breakdown mix'
          : cph < peerMean * 0.85
            ? 'Below peers'
            : 'In line with peers',
    reactiveSharePct: Math.round((breakdownCost / Math.max(1, total)) * 100),
    breakdowns: orders.filter((w) => w.wo_type === 'Breakdown').length,
    preventive: orders.filter((w) => w.wo_type === 'Preventive').length,
    late: orders.filter((w) => w.status.includes('late')).length,
    labourRate: rates.labour,
    dc: rates.dc,
    freightDays: rates.freight_days,
    lostProductionRate: rates.lost[f.cls] ?? null,
    rows: workHistory(machineId, ctx)
      .rows.filter((r) => r.order.wo_type !== 'Preventive')
      .slice(0, 25),
  };
}

// ---------------------------------------------------------------------------
// RMS
// ---------------------------------------------------------------------------

/**
 * Demo line 2240. `rmsView(id)` — the RMS health record for a machine.
 *
 * The RMS feed identifies eleven machines by a vendor short code (`'L14'`)
 * rather than the fleet id (`'L-02'`); `IDS` is that alias table. Resolve both
 * directions: the registry may be keyed by either, so try the fleet id, then
 * any alias that resolves to it.
 */
export function rmsFor(
  machineId: string,
  registry: Readonly<Record<string, RmsMachineHealth>>,
): RmsMachineHealth | null {
  const direct = registry[machineId];
  if (direct) return direct;
  const alias = Object.keys(IDS).find((short) => IDS[short] === machineId);
  return (alias && registry[alias]) || null;
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** One connector's documents on the machine's Documents tab. */
export interface DocumentGroup {
  readonly connector: string;
  readonly entries: readonly { key: string; ref: string; label: string; kind: string }[];
}

/**
 * Demo line 2242. `machineTab('sources')` — the lake index for one machine,
 * grouped by source system, biggest group first.
 *
 * A `[ref, file, kind, conn]` tuple can repeat across systems, so the key
 * carries the connector and the position as well as the ref.
 */
export function documentGroups(entries: readonly DocIndexEntry[]): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup['entries'][number][]>();
  entries.forEach(([ref, file, kind, conn], i) => {
    const bucket = groups.get(conn) ?? [];
    bucket.push({ key: `${conn}#${i}#${ref}`, ref, label: file, kind });
    groups.set(conn, bucket);
  });
  return [...groups.entries()]
    .map(([connector, list]) => ({ connector, entries: list }))
    .sort((a, b) => b.entries.length - a.entries.length);
}

// ---------------------------------------------------------------------------
// Telemetry
// ---------------------------------------------------------------------------

/**
 * Demo line 2130. `METRIC_INFO` — the tracked metric's display name, unit and
 * chart axis title. The telemetry tab uses the longer names the demo spells
 * out at line 2209, so those are what is kept here.
 */
const METRIC_TITLE: Readonly<
  Record<DailyMetricName, { name: string; unit: string; axis: string }>
> = {
  percussion_pressure_bar: {
    name: 'Percussion pressure at full collar',
    unit: 'bar',
    axis: 'Pressure',
  },
  hyd_oil_temp_c: { name: 'Hydraulic oil temperature', unit: '°C', axis: 'Temperature' },
  rear_brake_temp_c: { name: 'Rear brake temperature', unit: '°C', axis: 'Temperature' },
  penetration_rate_m_h: {
    name: 'Penetration rate while drilling',
    unit: 'm/h',
    axis: 'Penetration rate',
  },
};

/** One plotted day: `MM-DD` label plus value. */
export interface DayPoint {
  readonly label: string;
  readonly value: number;
}

/** The live-feed chart on the telemetry tab, as data. */
export interface LiveFeedView {
  /** `HH:mm` per sample. */
  readonly labels: readonly string[];
  readonly values: readonly number[];
  readonly limit: number;
  readonly seriesName: string;
  /** Rendered y-axis domain, fixed from the whole shift so a replay never rescales. */
  readonly min: number;
  readonly max: number;
  readonly caption: string;
  /** Alarm flags per sample (`''` for none), for the replay's running count. */
  readonly alarms: readonly string[];
}

/** The S-09 live RMS torque chart, as data. */
export interface LiveTorqueView {
  readonly labels: readonly string[];
  readonly values: readonly number[];
}

/** The telemetry tab, as data. Demo line 2209. */
export interface MachineTelemetryView {
  readonly metric: {
    readonly title: string;
    readonly unit: string;
    readonly axis: string;
    readonly subtitle: string;
    readonly points: readonly DayPoint[];
    readonly limit: number | null;
    readonly limitLabel: string;
  } | null;
  readonly events: {
    readonly labels: readonly string[];
    readonly alarms: readonly number[];
    readonly abuse: readonly number[];
    /** `'W-BRK-TEMP ×4, …'` or `'none'`. */
    readonly codes: string;
  };
  /** The open alert's shift-end trend, when this machine has one. */
  readonly alertTrend: {
    readonly note: string;
    readonly metric: string;
    readonly unit: string;
    readonly limit: number;
    /** `null` breaks the line — a day with no reading, not a zero. */
    readonly points: readonly { readonly label: string; readonly value: number | null }[];
  } | null;
  /** Shift-end sites have no live feed; the tab explains why. */
  readonly isLiveSite: boolean;
  readonly mineName: string;
}

/** Inputs the telemetry tab needs beyond the eager register. */
export interface TelemetryInputs {
  readonly dailyMetric: readonly DailyMetricRow[];
  readonly shiftDaily: readonly ShiftDailyRow[];
  readonly eventsDaily: readonly EventDailyRow[];
  readonly alertSeries: TrendSeriesRegistry;
}

const mmdd = (date: string): string => date.slice(5, 10);
const hhmm = (ts: string): string => ts.slice(11, 16);

/**
 * Demo line 2209. `machineTab('telemetry')` — the key metric's daily mean
 * with its limit, alarms and abuse per day, and the open alert's trend. The
 * live MQTT feed and the S-09 torque trace come from their own domains and
 * are projected by {@link liveFeedFor} and {@link s09Torque}.
 *
 * The demo draws the metric's limit from the class, not the metric: 240 °C on
 * trucks, the 85 °C alarm on loaders, none on drills. Kept as written.
 */
export function machineTelemetry(machineId: string, input: TelemetryInputs): MachineTelemetryView {
  const f = BY_ID[machineId];
  const m = MINES[f.mine];
  const isLiveSite = m.data_mode === 'Live';

  const dm = input.dailyMetric.filter((d) => d.machine_id === machineId);
  const sd = input.shiftDaily.filter((s) => s.machine_id === machineId);

  const codes = new Map<string, number>();
  for (const e of input.eventsDaily) {
    if (e.machine_id === machineId) codes.set(e.code, (codes.get(e.code) ?? 0) + e.n);
  }

  const info = dm[0] ? METRIC_TITLE[dm[0].metric] : null;
  const limit = f.cls === 'truck' ? 240 : f.cls === 'lhd' ? 85 : null;

  const aser = input.alertSeries[machineId] ?? null;

  return {
    metric: info
      ? {
          title: `${info.name} · daily mean`,
          unit: info.unit,
          axis: `${info.axis} (${info.unit})`,
          subtitle: `${
            isLiveSite
              ? f.cls === 'sdrill'
                ? 'RMS feed aggregated per day'
                : 'Live telematics aggregated per day'
              : 'Pitram shift-end export, consolidated per day'
          } · 29 Aug – 9 Sep · ${dm.length} source rows`,
          points: dm.map((d) => ({ label: mmdd(d.date), value: d.value })),
          limit,
          limitLabel: f.cls === 'truck' ? '240 °C limit' : f.cls === 'lhd' ? '85 °C alarm' : '',
        }
      : null,
    events: {
      labels: sd.map((s) => mmdd(s.date)),
      alarms: sd.map((s) => s.alarms),
      abuse: sd.map((s) => s.abuse),
      codes: [...codes.entries()].map(([k, v]) => `${k} ×${v}`).join(', ') || 'none',
    },
    alertTrend: aser
      ? {
          note: aser.note,
          metric: aser.metric,
          unit: aser.unit,
          limit: aser.limit,
          points: aser.rows.map(([date, value]) => ({ label: mmdd(date), value })),
        }
      : null,
    isLiveSite,
    mineName: m.name,
  };
}

/**
 * Demo line 2218. The live feed: rear brake temperature against 240 °C on a
 * truck, intake air against the 48 °C derate threshold on a loader.
 *
 * The y domain is the demo's `floor(min − 1)` / `ceil(max + 1)` over the whole
 * shift and the limit, computed once here so a replay that reveals the samples
 * one by one never rescales the axis under the reader.
 */
export function liveFeedFor(machineId: string, registry: LiveReplayRegistry): LiveFeedView | null {
  const rows = registry[machineId];
  if (!rows?.length) return null;
  return liveFeed(BY_ID[machineId].cls, rows);
}

function liveFeed(cls: string, rows: readonly LiveSample[]): LiveFeedView {
  const truck = cls === 'truck';
  const values = rows.map((r) => ('brake_temp_rr_c' in r ? r.brake_temp_rr_c : r.intake_air_c));
  const limit = truck ? 240 : 48;
  const all = [...values, limit];
  return {
    labels: rows.map((r) => hhmm(r.ts)),
    values,
    limit,
    seriesName: truck ? 'rear brake temp °C' : 'intake air °C',
    min: Math.floor(Math.min(...all) - 1),
    max: Math.ceil(Math.max(...all) + 1),
    caption: truck
      ? 'rear brake temperature with the 240 °C limit'
      : 'intake air temperature with the 48 °C derate threshold',
    alarms: rows.map((r) => r.alarm),
  };
}

/**
 * Demo line 2217. S-09's rotation torque over the night shift, drilling
 * samples only — pipe changes read zero torque and would saw the line.
 */
export function s09Torque(rows: readonly RmsLiveDrillSample[]): LiveTorqueView {
  const drilling = rows.filter((r) => r.state === 'Drilling');
  return {
    labels: drilling.map((r) => hhmm(r.ts)),
    values: drilling.map((r) => r.rotation_torque_knm),
  };
}

// ---------------------------------------------------------------------------
// Energy
// ---------------------------------------------------------------------------

/** The energy tab, as data. Demo line 2224. */
export interface MachineEnergyView {
  readonly drive: string;
  /** `'38 L/h diesel nominal · 12 kWh/h electric nominal'`. */
  readonly nominal: string;
  readonly dieselL: number;
  readonly dieselPrice: number;
  readonly kwh: number;
  readonly powerSource: string;
  readonly powerPrice: number;
  readonly co2PerHour: number;
  readonly peerCo2PerHour: number;
  readonly energyCost: number;
  readonly gridFactor: number;
  readonly hours: number;
  /** `kg CO₂-e per operating hour = (… L × 2.68 + … kWh × …) ÷ … h = …`. */
  readonly calc: string;
  readonly labels: readonly string[];
  readonly dieselPerDay: readonly number[];
  readonly kwhPerDay: readonly number[];
  readonly co2PerDay: readonly number[];
}

/**
 * Demo line 2224. `machineTab('energy')` — drive, 12-day diesel and
 * electricity, emissions intensity against class peers, and the two daily
 * charts.
 *
 * Returns `null` when the machine has no energy row, which the fleet does not
 * produce but the tab handles rather than assuming.
 */
export function machineEnergy(
  machineId: string,
  energy: readonly MachineEnergy[],
  shiftDaily: readonly ShiftDailyRow[],
): MachineEnergyView | null {
  const f = BY_ID[machineId];
  const e = energy.find((x) => x.machine_id === machineId);
  const fac = ENERGY_FACTORS.find((x) => x.mine === f.mine);
  if (!e || !fac) return null;

  const classDef = CLASSES[f.model];
  const peers = energy.filter(
    (x) => BY_ID[x.machine_id]?.cls === f.cls && x.machine_id !== machineId,
  );
  const peerCo2PerHour = peers.length
    ? peers.reduce((a, x) => a + x.co2_kg_per_hour, 0) / peers.length
    : 0;
  const sd = shiftDaily.filter((s) => s.machine_id === machineId);

  const nominal = [
    classDef.diesel_l_h ? `${classDef.diesel_l_h} L/h diesel nominal` : 'no diesel',
    classDef.kwh_h ? `${classDef.kwh_h} kWh/h electric nominal` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    drive: f.drive,
    nominal,
    dieselL: e.diesel_l,
    dieselPrice: fac.diesel_aud_per_l,
    kwh: e.energy_kwh,
    powerSource: fac.power_source,
    powerPrice: fac.power_aud_per_kwh,
    co2PerHour: e.co2_kg_per_hour,
    peerCo2PerHour,
    energyCost: e.energy_cost_aud,
    gridFactor: fac.grid_kgco2_per_kwh,
    hours: e.hours,
    calc: `kg CO₂-e per operating hour = (${groupNum(e.diesel_l)} L × 2.68 + ${groupNum(e.energy_kwh)} kWh × ${fac.grid_kgco2_per_kwh}) ÷ ${e.hours} h = ${e.co2_kg_per_hour}`,
    labels: sd.map((s) => mmdd(s.date)),
    dieselPerDay: sd.map((s) => s.diesel_l),
    kwhPerDay: sd.map((s) => s.kwh),
    co2PerDay: sd.map((s) => s.co2_kg),
  };
}

// ---------------------------------------------------------------------------
// Parts & stock
// ---------------------------------------------------------------------------

/** One row of the parts tab. */
export interface MachinePartRow {
  readonly part: CataloguePart;
  /** Site-store line, or `null` when the site does not stock it. */
  readonly site: InventoryLine | null;
  readonly dc: InventoryLine | null;
  readonly belowMin: boolean;
}

/** The parts tab, as data. Demo line 2228. */
export interface MachinePartsView {
  readonly store: string;
  readonly dc: string;
  readonly rows: readonly MachinePartRow[];
  readonly belowMin: number;
}

/**
 * Demo line 2228. `machineTab('parts')` — every catalogue part that fits this
 * machine's class (or every class), with the site store and the serving DC.
 * Red when the site store is below its minimum.
 */
export function machineParts(
  machineId: string,
  parts: readonly CataloguePart[],
  inventory: readonly InventoryLine[],
): MachinePartsView {
  const f = BY_ID[machineId];
  const store = `${f.mine} site store`;
  const dc = RATES[f.mine].dc;
  const rows = parts
    .filter((p) => p.fits === 'all' || p.fits === f.cls)
    .map((part): MachinePartRow => {
      const site =
        inventory.find((i) => i.location === store && i.part_no === part.part_no) ?? null;
      return {
        part,
        site,
        dc: inventory.find((i) => i.location === dc && i.part_no === part.part_no) ?? null,
        belowMin: site != null && site.qty_on_hand < site.min_level,
      };
    });
  return { store, dc, rows, belowMin: rows.filter((r) => r.belowMin).length };
}

// ---------------------------------------------------------------------------
// Machine history — the merged timeline
// ---------------------------------------------------------------------------

/** Which stream a timeline event came from. */
export type TimelineKind = 'workOrder' | 'fault' | 'case' | 'email';

/** One event on the merged machine history. */
export interface TimelineEvent {
  readonly key: string;
  readonly kind: TimelineKind;
  /** ISO-ish timestamp the list sorts on. */
  readonly at: string;
  readonly date: string;
  /** The small caption under the date — the WO type, `Fault`, `Case`, `Email`. */
  readonly tag: string;
  /** Set for work orders. */
  readonly workOrder?: WorkHistoryRow;
  /** Set for faults, cases and emails. */
  readonly title?: string;
  readonly meta?: string;
  readonly detail?: string;
  /** Set for emails — the `.eml` file the drawer opens. */
  readonly file?: string;
}

/** The merged history, as data. */
export interface MachineTimeline {
  readonly events: readonly TimelineEvent[];
  readonly summary: WorkHistorySummary;
}

/** Streams beyond the work orders. Each is optional until its domain lands. */
export interface TimelineInputs {
  readonly faults: readonly CriticalEvent[] | null;
  readonly cases: readonly ServiceCase[] | null;
  readonly emails: readonly EmailHeader[] | null;
}

/**
 * Demo line 2200. `machineTab('history')` — work orders, critical faults,
 * service cases and email threads on one list, newest first. The demo caps the
 * list at 80; so does this.
 *
 * Filtering by component narrows only the work orders — the other streams
 * carry no component — which is the demo's behaviour.
 *
 * Emails join on the subject naming the machine (`subject.includes(id)`), as
 * the demo does; there is no machine column on an email header.
 */
export function machineTimeline(
  machineId: string,
  ctx: WorkOrderCtx,
  streams: TimelineInputs,
  component?: string | null,
): MachineTimeline {
  const summary = workHistory(machineId, ctx, component);

  const events: TimelineEvent[] = [
    ...summary.rows.map((row): TimelineEvent => ({
      key: `wo:${row.key}`,
      kind: 'workOrder',
      at: row.order.created,
      date: row.date,
      tag: row.order.wo_type,
      workOrder: row,
    })),
    ...(streams.faults ?? [])
      .filter((e) => e.machine_id === machineId)
      .map((e, i): TimelineEvent => ({
        key: `fault:${e.timestamp}#${i}`,
        kind: 'fault',
        at: e.timestamp,
        date: e.timestamp.slice(0, 10),
        tag: 'Fault',
        title: e.code,
        meta: e.severity,
        detail: e.source,
      })),
    ...(streams.cases ?? [])
      .filter((c) => c.machine_id === machineId)
      .map((c): TimelineEvent => ({
        key: `case:${c.case_no}`,
        kind: 'case',
        at: c.opened,
        date: c.opened.slice(0, 10),
        tag: 'Case',
        title: c.case_no,
        meta: c.type,
        detail: `${c.subject} — ${c.status}`,
      })),
    ...(streams.emails ?? [])
      .filter((e) => e.subject.includes(machineId))
      .map((e): TimelineEvent => ({
        key: `email:${e.file}`,
        kind: 'email',
        at: e.date,
        date: e.date.slice(0, 10),
        tag: 'Email',
        title: e.subject,
        meta: e.from_,
        file: e.file,
      })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return { events: events.slice(0, 80), summary };
}

/**
 * Demo line 2208. The history tab's availability line — each day's hours over
 * the 19 scheduled (two 9.5 h shifts), as a percentage to one decimal.
 */
export function availabilitySeries(
  machineId: string,
  shiftDaily: readonly ShiftDailyRow[],
): DayPoint[] {
  return shiftDaily
    .filter((s) => s.machine_id === machineId)
    .map((s) => ({ label: mmdd(s.date), value: +((s.hours / 19) * 100).toFixed(1) }));
}

// ---------------------------------------------------------------------------
// Header — the jump list and the ←/→ step
// ---------------------------------------------------------------------------

/** One option in the header's jump list. */
export interface MachineJumpOption {
  readonly id: string;
  /** `'L-02 · ML-21 · Kalbarri Gold · Very poor'` — the demo's option text. */
  readonly label: string;
}

/**
 * Demo line 2178. `#mh-pick` — every machine in the fleet, in register order,
 * labelled `id · model · site · condition`.
 */
export const machineJumpOptions = (): MachineJumpOption[] =>
  FLEET.map((x) => ({
    id: x.machine_id,
    label: `${x.machine_id} · ${x.model} · ${MINES[x.mine].name} · ${CONDITION_BAND_LABEL[statusOf(x.machine_id)]}`,
  }));
